import { useState, useEffect } from "react"
import { toast } from "sonner"
import { Download, Printer, ExternalLink, QrCode } from "lucide-react"
import { useTranslation } from "react-i18next"
import * as QRCode from "qrcode"
import type { ArealResponse } from "@fohet/caravan-booker-shared/types"
import { primaryBtnClass, sectionClass, sectionHeaderClass, labelClass } from "@/lib/styleClasses"

export const QrCodeSection = ({ areal }: { areal: ArealResponse }) => {
    const { t } = useTranslation()
    const [pngDataUrl, setPngDataUrl] = useState<string | null>(null)
    const campUrl = `${window.location.origin}/a/${areal.slug}`

    useEffect(() => {
        let cancelled = false
        void QRCode.toDataURL(campUrl, {
            width: 300,
            margin: 2,
            color: { dark: "#2d2d2d", light: "#ffffff" },
        }).then((url) => {
            if (!cancelled) setPngDataUrl(url)
        })
        return () => { cancelled = true }
    }, [campUrl])

    const handleDownloadPng = () => {
        if (!pngDataUrl) return
        const a = document.createElement("a")
        a.href = pngDataUrl
        a.download = `${areal.slug}-qr.png`
        a.click()
    }

    const handleDownloadSvg = async () => {
        try {
            const svgString = await QRCode.toString(campUrl, {
                type: "svg",
                margin: 2,
                color: { dark: "#2d2d2d", light: "#ffffff" },
            })
            const blob = new Blob([svgString], { type: "image/svg+xml" })
            const url = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = `${areal.slug}-qr.svg`
            a.click()
            URL.revokeObjectURL(url)
        } catch {
            toast.error(t("admin.settings.qrDownloadFailed"))
        }
    }

    const handlePrintPoster = async () => {
        try {
            const svgString = await QRCode.toString(campUrl, {
                type: "svg",
                width: 400,
                margin: 2,
                color: { dark: "#2d2d2d", light: "#ffffff" },
            })
            const win = window.open("", "_blank")
            if (!win) {
                toast.error(t("admin.settings.qrPrintPopupBlocked"))
                return
            }
            win.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${areal.name} — QR</title>
  <style>
    @page { size: A4; margin: 40px; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      background: #ffffff;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      padding: 40px 20px;
      color: #2d2d2d;
    }
    .brand {
      font-size: 13px;
      font-weight: 900;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: #76d248;
      margin-bottom: 32px;
    }
    .qr-wrapper {
      border: 3px solid #2d2d2d;
      border-radius: 24px;
      padding: 20px;
      background: #ffffff;
      box-shadow: 0 8px 32px rgba(0,0,0,0.10);
    }
    .qr-wrapper svg {
      display: block;
      width: 320px;
      height: 320px;
    }
    .camp-name {
      font-size: 28px;
      font-weight: 900;
      letter-spacing: -0.02em;
      color: #2d2d2d;
      text-align: center;
      margin-top: 28px;
      max-width: 400px;
    }
    .scan-hint {
      font-size: 14px;
      font-weight: 700;
      color: #64748b;
      text-align: center;
      margin-top: 12px;
      letter-spacing: 0.03em;
    }
    .url {
      font-size: 11px;
      font-weight: 600;
      color: #94a3b8;
      margin-top: 8px;
      letter-spacing: 0.01em;
    }
    @media print {
      body { min-height: unset; padding: 0; }
    }
  </style>
</head>
<body>
  <div class="brand">Kempi</div>
  <div class="qr-wrapper">${svgString}</div>
  <div class="camp-name">${areal.name}</div>
  <div class="scan-hint">${t("admin.settings.qrPosterScanHint")}</div>
  <div class="url">${campUrl}</div>
  <script>window.onload = function() { window.print(); }<\/script>
</body>
</html>`)
            win.document.close()
        } catch {
            toast.error(t("admin.settings.qrPrintFailed"))
        }
    }

    return (
        <div className={sectionClass}>
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <QrCode className="size-5 text-slate-400" />
                    <h2 className={sectionHeaderClass}>{t("admin.settings.sectionQrCode")}</h2>
                </div>
                <a
                    href={`/a/${areal.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm font-black text-brand hover:text-brand-dark transition-colors"
                >
                    <ExternalLink className="size-3.5" />
                    {t("admin.settings.qrViewCamp")}
                </a>
            </div>

            <p className="text-sm font-bold text-slate-500">{t("admin.settings.qrDescription")}</p>

            <div className="flex flex-col sm:flex-row items-start gap-6">
                <div className="shrink-0 rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-md p-3">
                    {pngDataUrl ? (
                        <img
                            src={pngDataUrl}
                            alt={t("admin.settings.qrImageAlt", { name: areal.name })}
                            className="size-36 block"
                        />
                    ) : (
                        <div className="size-36 flex items-center justify-center text-slate-300">
                            <QrCode className="size-12" />
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-3">
                    <div>
                        <p className={labelClass}>{t("admin.settings.qrCampUrl")}</p>
                        <p className="text-sm font-bold text-slate-700 break-all">{campUrl}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={handleDownloadPng}
                            disabled={!pngDataUrl}
                            className={primaryBtnClass}
                        >
                            <Download className="size-3.5" />
                            {t("admin.settings.qrDownloadPng")}
                        </button>
                        <button
                            type="button"
                            onClick={() => { void handleDownloadSvg() }}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-[28px] bg-slate-100 text-slate-600 font-bold text-sm hover:bg-slate-200 transition-colors active:scale-95"
                        >
                            <Download className="size-3.5" />
                            {t("admin.settings.qrDownloadSvg")}
                        </button>
                        <button
                            type="button"
                            onClick={() => { void handlePrintPoster() }}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-[28px] bg-charcoal text-white font-black text-sm hover:bg-slate-700 transition-colors active:scale-95"
                        >
                            <Printer className="size-3.5" />
                            {t("admin.settings.qrPrintPoster")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
